import React, { useEffect, useState } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Grid,
  Autocomplete,
  Alert,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import PropTypes from "prop-types";
import axios from "axios";

const StaffAllocationDialog = ({
  open,
  onClose,
  examScheduleMasterId,
  exam_slot_id,
  exam_slot_desc,
  from_exam_date,
  showSnackbar,
  internalBaseUrl,
}) => {
  const user = JSON.parse(sessionStorage.getItem("user"));
  const clientId = user?.client_id;
  const collegeId = user?.college_id;
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const [pageSize, setPageSize] = useState(10);
  const [allocations, setAllocations] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState("");
  const [observerName, setObserverName] = useState("");
  const [spare1Name, setSpare1Name] = useState("");
  const [spare2Name, setSpare2Name] = useState("");
  const getAuthHeaders = () => {
    const storedToken = sessionStorage.getItem("token");
    if (!storedToken) return {};
    try {
      const parsed = JSON.parse(storedToken);
      return { Authorization: `Bearer ${typeof parsed === "string" ? parsed : storedToken}` };
    } catch {
      return { Authorization: `Bearer ${storedToken}` };
    }
  };
  function convertToYMD(dateStr) {
    const [day, month, year] = dateStr.split("/");
    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  }

  // Fetch allocation data
  const fetchAllocations = async () => {
    try {
      setLoading(true);
      setFetchError("");
      const res = await axios.get(
        internalBaseUrl ||
          `${API_BASE_URL}/api/exam-room-staff-allocation/${clientId}/${collegeId}`,
        {
          params: {
            ...(!internalBaseUrl ? { exam_schedule_master_id: examScheduleMasterId } : {}),
            exam_slot_id: exam_slot_id,
            from_exam_date: from_exam_date,
            _: Date.now(),
          },
          headers: {
            ...getAuthHeaders(),
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
            "If-Modified-Since": "0",
          },
        }
      );

      if (res.data.success) {
        const responseData = res.data.data;
        setAllocations(
          Array.isArray(responseData)
            ? responseData
            : Array.isArray(responseData?.items)
            ? responseData.items
            : []
        );
      } else {
        setAllocations([]);
        const message = res.data.message || "No allocations found";
        setFetchError(message);
        showSnackbar?.(message, "warning");
      }
    } catch (err) {
      console.error(err);
      setAllocations([]);
      const message = err.response?.data?.message || "Error fetching allocations";
      setFetchError(message);
      showSnackbar?.(message, "error");
    } finally {
      setLoading(false);
    }
  };

  // Fetch staff list
  const fetchStaffList = async () => {
    try {
      const staff_type = "T";
      const res = await axios.get(
        `${API_BASE_URL}/api/staff/${clientId}/${collegeId}/${staff_type}`,
        { headers: getAuthHeaders() }
      );

      if (res.data.success) {
        setStaffList(res.data.data || []);
      } else {
        setStaffList([]);
      }
    } catch (err) {
      console.error(err);
      showSnackbar?.("Error fetching staff list", "error");
    }
  };

  useEffect(() => {
    if (open) fetchStaffList();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (!from_exam_date || !exam_slot_id) {
      setAllocations([]);
      return;
    }
    if (!internalBaseUrl && !examScheduleMasterId) return;
    fetchAllocations();
  }, [open, internalBaseUrl, examScheduleMasterId, from_exam_date, exam_slot_id]);

  // Handle staff dropdown change
  const handleStaffChange = (rowId, newStaffId) => {
    setAllocations((prev) =>
      prev.map((row) =>
        (row.internal_room_staff_allocation_id || row.room_staff_allocation_id) === rowId
          ? { ...row, staff_id: newStaffId }
          : row
      )
    );
  };

  // Confirm a single row
  const handleConfirmRow = async (row) => {
    try {
      await axios.put(
        internalBaseUrl
          ? `${internalBaseUrl}/${row.internal_room_staff_allocation_id}`
          : `${API_BASE_URL}/api/exam-room-staff-allocation/${clientId}/${collegeId}/${row.room_staff_allocation_id}`,
        { staff_id: row.staff_id }
      );

      showSnackbar?.(`Confirmed allocation for Room ${row.room?.room_no}`, "success");
    } catch (err) {
      console.error(err);
      showSnackbar?.(err.response?.data?.message || "Failed to confirm allocation", "error");
    }
  };

  // Confirm all rows
  const handleConfirmAll = async () => {
    try {
      await axios.put(
        internalBaseUrl
          ? `${internalBaseUrl}/bulk`
          : `${API_BASE_URL}/api/exam-room-staff-allocation/bulk/${clientId}/${collegeId}`,
        { allocations }
      );
      showSnackbar?.("All Staff allocations confirmed", "success");
    } catch (err) {
      console.error(err);
      showSnackbar?.(
        err.response?.data?.message || "Failed to confirm all Staff allocations",
        "error"
      );
    }
  };
  const handleDeleteRow = async (row) => {
    if (!window.confirm("Are you sure you want to delete this allocation?")) return;

    try {
      await axios.delete(
        internalBaseUrl
          ? `${internalBaseUrl}/${row.internal_room_staff_allocation_id}`
          : `${API_BASE_URL}/api/exam-room-staff-allocation/${clientId}/${collegeId}/${row.room_staff_allocation_id}`
      );

      showSnackbar?.("Allocation deleted successfully", "success");

      // Refresh table
      fetchAllocations();
    } catch (err) {
      console.error(err);
      showSnackbar?.("Failed to delete allocation", "error");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };
  // Download PDF
  const handleDownloadPDF = (observerName) => {
    const doc = new jsPDF("p", "mm", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();

    const collegeName = allocations[0]?.college?.college_name || "-";

    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text(String(collegeName), pageWidth / 2, 15, { align: "center" });

    doc.setFontSize(12);
    doc.text("Date wise Room-Staff Allocation Report", pageWidth / 2, 23, {
      align: "center",
    });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);

    doc.text(`Exam Date: ${String(formatDate(from_exam_date))}`, 14, 32);
    doc.text(`Exam Slot: ${String(exam_slot_desc)}`, pageWidth - 14, 32, {
      align: "right",
    });

    const tableColumn = ["S.No", "Room No", "Staff Name", "Signature"];
    const signature = "";

    const tableRows = allocations.map((row, index) => {
      const staffName = staffList.find((s) => s.staff_id === row.staff_id)?.staff_name || "-";

      return [
        String(index + 1),
        String(row.room?.room_no || "-"),
        String(staffName),
        String(signature),
      ];
    });

    autoTable(doc, {
      startY: 40,
      head: [tableColumn],
      body: tableRows,
      theme: "grid",

      headStyles: {
        fillColor: [25, 118, 210],
        textColor: 255,
        halign: "center",
        lineColor: [0, 0, 0], // optional (header border)
        lineWidth: 0.2,
      },

      bodyStyles: {
        fontSize: 10,
        lineColor: [0, 0, 0], // optional (body border)
        lineWidth: 0.2,
      },
      columnStyles: {
        0: { halign: "center", cellWidth: 20 },
        1: { halign: "center", cellWidth: 40 },
        2: { cellWidth: 60 },
        3: { cellWidth: 60 },
      },
    });

    const finalY = doc.lastAutoTable.finalY + 10;
    const generatedAt = new Date().toLocaleString();
    doc.text(`Spare Name-1: ${spare1Name || "-"}`, 14, finalY);
    doc.text(`Spare Name-2: ${spare2Name || "-"}`, 14, finalY + 10);
    doc.text(`Observer Name: ${observerName || "-"}`, 14, finalY + 20);
    doc.setFontSize(9);

    doc.setFontSize(10);
    doc.text("_________________________", pageWidth - 70, finalY + 30);
    doc.text("Signature of Principal / Superintendent", pageWidth - 70, finalY + 30);

    doc.save(`Staff_Allocation_${String(from_exam_date)}_${String(exam_slot_desc)}.pdf`);
  };

  const columns = [
    { field: "sno", headerName: "S.No", flex: 0.3 },
    { field: "room_no", headerName: "Room No", flex: 1 },
    {
      field: "staff_id",
      headerName: "Staff",
      flex: 2,
      renderCell: (params) => (
        <Autocomplete
          size="medium"
          fullWidth
          options={staffList}
          getOptionLabel={(option) => option.staff_name || ""}
          value={staffList.find((s) => s.staff_id === params.row.staff_id) || null}
          onChange={(event, newValue) => {
            handleStaffChange(
              params.row.internal_room_staff_allocation_id || params.row.room_staff_allocation_id,
              newValue?.staff_id || ""
            );
          }}
          renderInput={(paramsInput) => (
            <TextField
              {...paramsInput}
              margin="dense"
              label="Select Staff"
              InputLabelProps={{ shrink: true }}
            />
          )}
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="contained"
            color="success"
            size="medium"
            onClick={() => handleConfirmRow(params.row)}
          >
            Confirm
          </Button>
          <Button
            variant="contained"
            color="error"
            size="medium"
            onClick={() => handleDeleteRow(params.row)}
          >
            Delete
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: "18px", color: "#555" }}>
          Staff Allocation on {from_exam_date ? formatDate(from_exam_date) : ""}
        </span>
        <Button
          variant="outlined"
          color="primary"
          size="medium"
          onClick={() => handleDownloadPDF(observerName)}
          sx={{
            textTransform: "none",
            fontSize: "0.875rem",
            borderRadius: "8px",
            paddingX: 2,
            color: "#3d2d2dff",
            borderColor: "#243be9ff",
          }}
        >
          Download PDF
        </Button>
      </DialogTitle>

      <DialogContent>
        {fetchError && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {fetchError}
          </Alert>
        )}
        <Box sx={{ mt: 2, mb: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                label="Observer Name"
                variant="outlined"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={observerName}
                onChange={(e) => setObserverName(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="Spare - 1"
                variant="outlined"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={spare1Name}
                onChange={(e) => setSpare1Name(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="Spare - 2"
                variant="outlined"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={spare2Name}
                onChange={(e) => setSpare2Name(e.target.value)}
              />
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ width: "100%", mt: 2 }}>
          <DataGrid
            rows={allocations.map((row, index) => ({
              id: row.internal_room_staff_allocation_id || row.room_staff_allocation_id,
              sno: index + 1,
              room_no: row.room?.room_no,
              staff_id: row.staff_id,
              ...row,
            }))}
            columns={columns}
            autoHeight
            loading={loading}
            pageSize={pageSize}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            rowsPerPageOptions={[5, 10, 25, 50]}
            disableRowSelectionOnClick
            pagination
            onCellClick={(params, event) => event.stopPropagation()}
            onRowClick={(params, event) => event.stopPropagation()}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{
            backgroundColor: "#1976d2",
            color: "#fff",
            "&:hover": { backgroundColor: "#1565c0" },
          }}
        >
          Close
        </Button>

        <Button
          variant="contained"
          color="primary"
          onClick={handleConfirmAll}
          sx={{
            backgroundColor: "#1976d2",
            color: "#fff",
            "&:hover": { backgroundColor: "#1565c0" },
          }}
        >
          Confirm All
        </Button>
      </DialogActions>
    </Dialog>
  );
};

StaffAllocationDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  examScheduleMasterId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  exam_slot_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  from_exam_date: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  exam_slot_desc: PropTypes.string,
  showSnackbar: PropTypes.func,
  internalBaseUrl: PropTypes.string,
};

export default StaffAllocationDialog;
